import { getSeoRoute } from '../seo/siteRoutes.js';
import {
  captureWhatsAppEnquiryPayload,
  createWhatsAppClickEventId as createEventId,
  isWhatsAppEnquiryAttributionEnabled,
  postWhatsAppEnquiry,
  readWhatsAppConsent,
  stageWhatsAppEnquiry,
  WHATSAPP_SURFACES as SURFACES,
} from './whatsappEnquiry.js';
import { canRetryWhatsAppSearchEntry, reserveWhatsAppSearchSitelinkEntry } from './whatsappSearchEntry.js';

const GA4_MEASUREMENT_ID = 'G-43KTYPJPNM';
// Separate Google Ads outbound-click action 7755859088; never a purchase or lead.
const GOOGLE_ADS_SEND_TO = 'AW-17646508237/y-0nCJCJpPIcEM2xwd5B';
const EVENT_ID_PATTERN = /^wc_(?:[0-9a-f]{32}|[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12})$/;
const scheduledEvents = new WeakSet();

function queueEvent(sender, name, payload) {
  if (!sender) return { queued: false, reason: 'gtag_unavailable' };
  try {
    sender('event', name, payload);
    return { queued: true };
  } catch (_) {
    return { queued: false, reason: 'gtag_exception' };
  }
}

// A queued tag is an outbound-click signal, not evidence of a WhatsApp message.
export function trackWhatsAppClick({
  surface,
  pathname,
  gtag,
  adsSendTo = GOOGLE_ADS_SEND_TO,
  eventId: suppliedEventId,
} = {}) {
  if (!SURFACES.has(surface)) return { queued: false, reason: 'invalid_surface' };

  const sender = typeof gtag === 'function'
    ? gtag
    : (typeof window !== 'undefined' && typeof window.gtag === 'function' ? window.gtag : null);
  const currentPath = pathname ?? (typeof window !== 'undefined' ? window.location?.pathname : '');
  // Known route paths exclude queries, fragments, and arbitrary private path data.
  const pagePath = currentPath ? getSeoRoute(currentPath)?.path : undefined;
  const eventId = EVENT_ID_PATTERN.test(suppliedEventId) ? suppliedEventId : createEventId();
  const ga4Payload = {
    send_to: GA4_MEASUREMENT_ID,
    event_id: eventId,
    whatsapp_surface: surface,
    destination: 'whatsapp',
    outbound: true,
  };
  if (pagePath) ga4Payload.page_path = pagePath;

  const ga4 = queueEvent(sender, 'whatsapp_click', ga4Payload);
  const ads = /^AW-\d+\/[A-Za-z0-9_-]+$/.test(adsSendTo)
    ? queueEvent(sender, 'conversion', { send_to: adsSendTo, transaction_id: eventId })
    : { queued: false, reason: 'ads_destination_unconfigured' };

  return { eventId, ga4, ads };
}

export function handleWhatsAppClick(event, surface, {
  enquiryEnabled = isWhatsAppEnquiryAttributionEnabled(),
} = {}) {
  const nativeEvent = event?.nativeEvent || event;
  if (!nativeEvent || typeof nativeEvent !== 'object' || !SURFACES.has(surface)) return;
  const isActivation = (nativeEvent.type === 'click' && nativeEvent.button === 0)
    || (nativeEvent.type === 'auxclick' && nativeEvent.button === 1);
  if (!isActivation || nativeEvent.isTrusted === false || event.defaultPrevented
    || nativeEvent.defaultPrevented || scheduledEvents.has(nativeEvent)) return;

  scheduledEvents.add(nativeEvent);
  let eventId = createEventId();
  const pathname = typeof window !== 'undefined' ? window.location?.pathname : '';
  let stage;
  let entry;
  try {
    // React currentTarget is available only during this listener. Capture and
    // change the anchor before the browser chooses its native destination.
    if (enquiryEnabled === true) {
      entry = reserveWhatsAppSearchSitelinkEntry({ enquiryEnabled });
      stage = stageWhatsAppEnquiry(event.currentTarget || nativeEvent.currentTarget, entry?.payload.enquiryRef);
      if (stage && entry) eventId = entry.payload.clickEventId;
      else if (entry) { entry.finish(false); entry = null; }
    }
  } catch (_) {
    entry?.finish(false);
    entry = null;
    // A failed enrichment must leave the usual WhatsApp navigation usable.
  }
  // A new task observes cancellation after all native event listeners finish.
  // Navigation proceeds immediately; a frozen page or blocked tag can miss telemetry.
  setTimeout(() => {
    try { stage?.restore(); } catch (_) {}
    if (event.defaultPrevented || nativeEvent.defaultPrevented) {
      entry?.finish(false);
      return;
    }
    entry?.finish(true);
    try {
      trackWhatsAppClick({ surface, pathname, eventId });
    } catch (_) {
      // Telemetry must not interfere with the native WhatsApp link.
    }
    try {
      // Read consent after the complete dispatch, including later consent updates.
      if (stage) {
        const currentConsent = readWhatsAppConsent(typeof window !== 'undefined' ? window.dataLayer : null);
        // A retry keeps the arrival payload immutable. A later denial can suppress
        // this retry; it never changes the existing row or creates a second entry.
        const enquiry = entry
          ? (canRetryWhatsAppSearchEntry(entry.payload, currentConsent) ? entry.payload : null)
          : captureWhatsAppEnquiryPayload({ enquiryRef: stage.enquiryRef, clickEventId: eventId, surface, pathname });
        if (enquiry) postWhatsAppEnquiry(enquiry);
      }
    } catch (_) {
      // Capture and persistence are independent of both tags and native navigation.
    }
  }, 0);
}
